export type TrendPoint = { x: number | null; y: number | null };
export type LabelBox = { x: number; y: number; width: number; height: number };

/** Exact segment/rectangle intersection, including a segment that crosses between sampled points. */
export function segmentHitsLabel(a: TrendPoint, b: TrendPoint, box: LabelBox): boolean {
  if (a.x == null || a.y == null || b.x == null || b.y == null) return false;
  if (
    Math.max(a.x, b.x) < box.x ||
    Math.min(a.x, b.x) > box.x + box.width ||
    Math.max(a.y, b.y) < box.y ||
    Math.min(a.y, b.y) > box.y + box.height
  )
    return false;
  const dx = b.x - a.x,
    dy = b.y - a.y;
  let enter = 0,
    leave = 1;
  for (const [p, q] of [
    [-dx, a.x - box.x],
    [dx, box.x + box.width - a.x],
    [-dy, a.y - box.y],
    [dy, box.y + box.height - a.y],
  ]) {
    if (p === 0) {
      if (q < 0) return false;
    } else if (p < 0) enter = Math.max(enter, q / p);
    else leave = Math.min(leave, q / p);
    if (enter > leave) return false;
  }
  return true;
}

/** Prefer nearby empty space; search the whole plot before accepting an obstructed position. */
export function placeDailyMarginLabel(
  points: ReadonlyArray<TrendPoint>,
  plot: LabelBox,
  size: { width: number; height: number },
  end: { x: number; y: number },
): LabelBox & { collisions: number } {
  const gap = 10,
    inset = 4;
  const left = plot.x + inset,
    top = plot.y + inset;
  const right = Math.max(left, plot.x + plot.width - size.width - inset);
  const bottom = Math.max(top, plot.y + plot.height - size.height - inset);
  const candidates = [
    { x: end.x - size.width - gap, y: end.y - size.height - gap },
    { x: end.x - size.width - gap, y: end.y + gap },
    { x: end.x + gap, y: end.y - size.height - gap },
    { x: end.x + gap, y: end.y + gap },
  ];
  for (let y = top; y <= bottom; y += 12) {
    for (let x = left; x <= right; x += 12) candidates.push({ x, y });
    candidates.push({ x: right, y });
  }
  candidates.push({ x: right, y: bottom });
  let best: LabelBox & { collisions: number } = { ...size, x: left, y: top, collisions: Infinity };
  let bestScore = Infinity;
  for (const candidate of candidates) {
    const box = {
      ...size,
      x: Math.min(right, Math.max(left, candidate.x)),
      y: Math.min(bottom, Math.max(top, candidate.y)),
    };
    // Include breathing room around the text and the stroke's width.
    const padded = { x: box.x - 4, y: box.y - 4, width: box.width + 8, height: box.height + 8 };
    let collisions = 0;
    for (let i = 0; i < points.length; i++) {
      if (segmentHitsLabel(points[i], points[i], padded)) collisions++;
      if (i > 0 && segmentHitsLabel(points[i - 1], points[i], padded)) collisions++;
    }
    const dx = Math.max(box.x - end.x, 0, end.x - box.x - box.width);
    const dy = Math.max(box.y - end.y, 0, end.y - box.y - box.height);
    const distance = Math.hypot(dx, dy);
    const centreDistance = Math.hypot(box.x + box.width / 2 - end.x, box.y + box.height / 2 - end.y);
    // Keep the endpoint visible, then minimise line obstruction, then proximity to the endpoint.
    const score = (distance < 8 ? 1e9 : 0) + collisions * 1e6 + distance * 10 + centreDistance;
    if (score < bestScore) {
      bestScore = score;
      best = { ...box, collisions };
    }
  }
  return best;
}
